import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import type { User, CycleWithComments } from './types';
import DocumentViewer from './DocumentViewer';
import DeadlinePill from './DeadlinePill';
import DeadlineEditor from './DeadlineEditor';
import AssignedDmEditor from './AssignedDmEditor';
import UnlinkLineageButton from './UnlinkLineageButton';
import RenameCycleButton from './RenameCycleButton';
import CancelCycleButton from './CancelCycleButton';
import MarkStepCompleteButton from './MarkStepCompleteButton';
import RevisionBanner from './RevisionBanner';
import { fetchContexts } from './useCurrentUserStep';
import type { ReviewerContext } from './types';
import './ContentReview.css';

interface PreScreenViewProps {
  user: User | null;
  cycleId: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  // Takes an optional flash string (ReviewInterface passes one after a
  // decision). Typed honestly so `onClick={onBack}` is a COMPILE error rather
  // than a runtime one: bare, React hands the click event to `flash`, the
  // dashboard renders it as a child, and React #31 unmounts the whole app —
  // a white page, which is exactly what happened in the 2026-08-21 prod E2E.
  onBack: (flash?: string) => void;
  // Phase 8C remainder — caller passes whether user can unlink lineage
  // (manage_submissions / admin role).
  canUnlinkLineage?: boolean;
  // Phase 8E — caller passes whether user can cancel this cycle. Mirrors
  // the DecisionView prop; PreScreenView surfaces Cancel too because a
  // cycle stuck in pending_prescreen is exactly the "unblock a stuck
  // lineage" scenario Cancel exists for (per plan doc D5).
  canCancelCycles?: boolean;
  // See DecisionView — same permission, same reason.
  canDeleteChildCycles?: boolean;
  // Phase 12B — edit-lock panel gating. Same shape as DecisionView.
  currentUserStep?: 'medical' | 'legal' | 'producer' | null;
  // See DecisionView — read-only viewer, separate edit question.
  canEditAnyComment?: boolean;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const PreScreenView: React.FC<PreScreenViewProps> = ({
  user, cycleId, getAuthHeaders, onBack,
  canUnlinkLineage = false, canCancelCycles = false, canDeleteChildCycles = false,
  currentUserStep = null,
  canEditAnyComment = false
}) => {
  const [data, setData] = useState<CycleWithComments | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');

  // Team members for reviewer selection — only those with review permission
  const [reviewers, setReviewers] = useState<{ _id: string; name: string; email: string }[]>([]);
  // Which candidates carry a reviewer DOMAIN. The permission bit put them in
  // this picker; only a ReviewerContext gives them a step, and assigning
  // someone without one silently produces a sequence that never waits on them
  // (2026-08-31 — found on a live cycle whose legal reviewer had no context).
  const [contexts, setContexts] = useState<ReviewerContext[]>([]);
  const [selectedReviewers, setSelectedReviewers] = useState<string[]>([]);
  // Pending deadline value from DeadlineEditor — submitted alongside the
  // prescreen action so the user doesn't have to save it separately.
  const [pendingDeadline, setPendingDeadline] = useState('');
  // Q2 v1 — optional destination override: tree paths fetched flat,
  // rendered as "Parent / Child" strings; empty = the Type's default.
  const [folderNodes, setFolderNodes] = useState<{ _id: string; name: string; parent: string | null; active: boolean }[]>([]);
  const [destinationNodeId, setDestinationNodeId] = useState('');

  // Bumped on every cycle refetch so DocumentViewer re-reads its children.
  // Without it, deleting an embedded child leaves its tab in the strip:
  // onChildDeleted refetches the cycle, but the children effect is keyed on
  // [cycle._id, cycle.contentType], neither of which changes (2026-08-21 audit).
  const [dataVersion, setDataVersion] = useState(0);
  // Which viewer tab is open. PreScreenView had no tab state until 2026-08-24,
  // so the pre-screen panel — assign reviewers, send to review, Fast Track —
  // rendered under EVERY tab, including an embedded item it has no business
  // on. Exactly the bug DecisionView had with the Final Decision panel: one
  // act for the whole review, presented as though it belonged to the tab you
  // happened to be standing on (Fredrik, pre-screening v0.2).
  const [activeTab, setActiveTab] = useState<string>('parent');
  // Children come from /parsed-content, which projects reviewedBy — enough for
  // the per-item panel without a fetch per child.
  const [children, setChildren] = useState<Array<{
    _id: string; contentItem: { name: string }; reviewedBy?: Array<{ reviewer?: { email?: string } }>;
  }>>([]);
  const [markingReviewed, setMarkingReviewed] = useState(false);

  // Same endpoint and same per-person array the reviewers, the producer and the
  // deciding DM use — an entry records THIS user's own look and stands in for
  // nobody else's.
  const toggleChildReviewed = async (childCycleId: string, makeReviewed: boolean) => {
    try {
      setMarkingReviewed(true);
      const headers = await getAuthHeaders();
      const url = `${backendUrl}/api/content-review/cycles/${childCycleId}/reviewed`;
      if (makeReviewed) await axios.post(url, {}, { headers });
      else await axios.delete(url, { headers });
      await fetchCycle();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update reviewed status');
    } finally {
      setMarkingReviewed(false);
    }
  };

  const fetchCycle = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const res = await axios.get(`${backendUrl}/api/content-review/cycles/${cycleId}`, { headers });
      setData(res.data);
      // Walk fix F8 (2026-08-12) — surface an already-stored override.
      // Without this a re-prescreen rendered "Type default" while the
      // stored override silently kept routing approvals.
      setDestinationNodeId((res.data?.cycle as any)?.destinationNodeId || '');
      try {
        const parsed = await axios.get(`${backendUrl}/api/content-review/cycles/${cycleId}/parsed-content`, { headers });
        setChildren(parsed.data?.children || []);
      } catch {
        // A missing children list must not blank the pre-screen itself.
        setChildren([]);
      }
      setDataVersion(v => v + 1);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch review cycle');
    } finally {
      setLoading(false);
    }
  }, [cycleId, getAuthHeaders]);

  useEffect(() => { fetchCycle(); }, [fetchCycle]);

  useEffect(() => {
    const fetchReviewers = async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${backendUrl}/api/team`, { headers });
        // Filter to only users with content_review.reviewer permission
        const eligible = (res.data || []).filter((m: any) =>
          m.permissions?.subcategories?.includes('content_review.reviewer') ||
          m.role === 'admin'
        );
        setReviewers(eligible);
        const treeRes = await axios.get(`${backendUrl}/api/content-review/folder-tree`, { headers });
        setFolderNodes((treeRes.data || []).filter((n: any) => n.active));
      } catch (err) {
        console.error('Error fetching team:', err);
      }
    };
    fetchReviewers();
    let cancelled = false;
    fetchContexts(getAuthHeaders).then(cs => { if (!cancelled) setContexts(cs); });
    return () => { cancelled = true; };
  }, [getAuthHeaders]);

  const handlePreScreen = async (decision: 'needs_mcl' | 'fast_track') => {
    if (decision === 'needs_mcl' && selectedReviewers.length === 0) {
      setError('Please select at least one reviewer for MCL review');
      return;
    }

    // If the user picked a deadline (saved or unsaved), submit it together
    // with the prescreen action so it persists even if they didn't click the
    // deadline's own Save button. Backend leaves the existing value alone if
    // we omit reviewDeadline.
    let deadlineIso: string | undefined;
    if (pendingDeadline) {
      const ts = new Date(`${pendingDeadline}T23:59:59`).getTime();
      if (Number.isNaN(ts) || ts <= Date.now()) {
        setError('Review deadline must be a valid future date');
        return;
      }
      deadlineIso = new Date(`${pendingDeadline}T23:59:59`).toISOString();
    }

    try {
      setSubmitting(true);
      setError(null);
      const headers = await getAuthHeaders();
      const body: Record<string, any> = {
        preScreenStatus: decision,
        reviewerIds: decision === 'needs_mcl' ? selectedReviewers : [],
        notes: notes.trim()
      };
      // Q2 v1 — per-cycle destination override. Walk fix F8 (2026-08-12):
      // send the field ALWAYS — explicit null clears a stored override
      // (the old truthy guard dropped '' client-side, so "Type default"
      // could never reset it; the backend maps falsy → null).
      body.destinationNodeId = destinationNodeId || null;
      if (deadlineIso) body.reviewDeadline = deadlineIso;
      await axios.put(`${backendUrl}/api/content-review/cycles/${cycleId}/prescreen`, body, { headers });
      onBack();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to pre-screen');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleReviewer = (id: string) => {
    setSelectedReviewers(prev =>
      prev.includes(id) ? prev.filter(r => r !== id) : [...prev, id]
    );
  };

  if (loading) return <div className="content-review" style={{ textAlign: 'center', padding: 40 }}>Loading...</div>;
  if (!data) return <div className="content-review"><p>Review cycle not found.</p></div>;

  const { cycle } = data;

  const POST_APPROVAL_LABELS: Record<string, string> = {
    content_hub: 'Content Hub upload',
    website_page: 'Website page build/update',
    portal_content: 'Portal content library (HubDB)',
    manual: 'Manual processing'
  };

  return (
    <div className="content-review">
      <button className="review-back-btn" onClick={() => onBack()}>&#8592; Back to Dashboard</button>

      <h2>Pre-Screen: {cycle.contentItem.name}</h2>
      {/* Fix pass 2026-08-10 (Finding 2) — surface producer.name. */}
      {cycle.producer?.name && (
        <div style={{ fontSize: 13, color: '#6c757d', marginTop: -8, marginBottom: 12 }}>
          Producer: <span style={{ color: '#495057', fontWeight: 500 }}>{cycle.producer.name}</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20 }}>
        <span className="review-status-badge pending_prescreen">Awaiting Pre-Screen</span>
        {cycle.contentItem.type && (
          <span className="review-meta-tag purpose">
            <span style={{ fontWeight: 400, marginRight: 4 }}>Type</span>{cycle.contentItem.type}
          </span>
        )}
        {cycle.contentItem.audience && (
          <span className="review-meta-tag audience">
            <span style={{ fontWeight: 400, marginRight: 4 }}>Audience</span>{cycle.contentItem.audience}
          </span>
        )}
        {/* v2 (D90) — topic + geography chips, aligned with the dashboard
            cards (raw slugs here; the dashboard resolves labels). Geo
            hidden for global — the default carries no information. */}
        {(cycle.contentItem as any).topic && (
          <span className="review-meta-tag" style={{ background: '#e7f5ec', color: '#1e6b3a' }}>
            <span style={{ fontWeight: 400, marginRight: 4 }}>Topic</span>{(cycle.contentItem as any).topic}
          </span>
        )}
        {(cycle.contentItem as any).geography && (cycle.contentItem as any).geography !== 'global' && (
          <span className="review-meta-tag" style={{ background: '#fdf1e0', color: '#8a5a00' }}>
            <span style={{ fontWeight: 400, marginRight: 4 }}>Geo</span>{(cycle.contentItem as any).geography}
          </span>
        )}
        {cycle.contentItem.version && (
          <span className="review-meta-tag version" title="Draft version (X.Y — approvals stamp V<major>.0 in the archive)">
            V{cycle.contentItem.version}
          </span>
        )}
        {/* 14e walk station 4 — editable source visible in-cycle (labels
            aligned with the dashboard cards). Read-only here; edits go
            through the dashboard Edit dialog. */}
        {(cycle.contentItem as any).sourceLink && (
          // vm: links have no navigable target — render a span, not an
          // href-less anchor (jsx-a11y/anchor-is-valid; Vercel cold builds
          // treat lint warnings as errors under CI=true).
          (cycle.contentItem as any).sourceLink.startsWith('vm:') ? (
            <span
              className="review-meta-tag"
              style={{ background: '#e8f0fe', color: '#1a58c2', fontWeight: 500 }}
              title={`Source (VM project — open on the render VM): ${(cycle.contentItem as any).sourceLink}`}
            >
              🔗 Source
            </span>
          ) : (
            <a
              className="review-meta-tag"
              href={(cycle.contentItem as any).sourceLink}
              target="_blank" rel="noopener noreferrer"
              style={{ background: '#e8f0fe', color: '#1a58c2', fontWeight: 500, textDecoration: 'none' }}
              title={`Editable source: ${(cycle.contentItem as any).sourceLink}`}
            >
              🔗 Source
            </a>
          )
        )}
        {(cycle.contentItem as any).sourceFileId && (
          <span className="review-meta-tag" style={{ background: '#e6f4ea', color: '#1e6f2c', fontWeight: 500 }}
            title={`Editable companion: ${(cycle.contentItem as any).sourceFileName || 'source file'}`}>
            📎 Source file
          </span>
        )}
        {(cycle.contentItem as any).noEditableSourceReason && (
          <span className="review-meta-tag" style={{ background: '#fff4e5', color: '#a15c00', fontWeight: 500 }}
            title={`Source waived — reason: ${(cycle.contentItem as any).noEditableSourceReason}`}>
            ⚠ No source ({(cycle.contentItem as any).noEditableSourceReason})
          </span>
        )}
        <DeadlinePill deadline={cycle.reviewDeadline} />
        {/* Phase 12E — step-complete action (same shape as DecisionView). */}
        <MarkStepCompleteButton
          cycle={cycle}
          currentUserStep={currentUserStep}
          getAuthHeaders={getAuthHeaders}
          onComplete={fetchCycle}
        />
        {/* Phase 8E — admin "Cancel cycle" available at pre-screen too. */}
        <CancelCycleButton cycle={cycle} canCancel={canCancelCycles} getAuthHeaders={getAuthHeaders} onComplete={fetchCycle} />
        {/* Phase 8C remainder — admin "Fork lineage" (rename + unlink) and
            standalone "Rename". Available pre-screen too, since metadata
            errors are most visible right when a DM is reviewing a fresh
            submission. */}
        <UnlinkLineageButton cycle={cycle} canUnlink={canUnlinkLineage} getAuthHeaders={getAuthHeaders} onComplete={fetchCycle} />
        <RenameCycleButton cycle={cycle} canRename={canUnlinkLineage} getAuthHeaders={getAuthHeaders} onComplete={fetchCycle} />
      </div>

      <RevisionBanner cycle={cycle} getAuthHeaders={getAuthHeaders} />

      {/* Submission notes (from web page submission) */}
      {cycle.submissionNotes && (
        <div style={{ background: '#f0f7ff', padding: 14, borderRadius: 8, borderLeft: '3px solid #123250', marginBottom: 16, fontSize: 14, color: '#333' }}>
          <strong style={{ fontSize: 12, color: '#123250', display: 'block', marginBottom: 4 }}>Submission Notes</strong>
          {cycle.submissionNotes}
        </div>
      )}

      {error && <div style={{ color: '#dc3545', padding: 12, background: '#f8d7da', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* File validation results — only for file-based content, not web pages */}
      {cycle.fileValidation && cycle.contentType !== 'webpage' && (
        <div className="review-section" style={{
          background: cycle.fileValidation.valid ? '#d4edda' : '#fff3cd',
          padding: 16, borderRadius: 10, marginBottom: 20
        }}>
          <h4 style={{ margin: '0 0 8px', color: cycle.fileValidation.valid ? '#155724' : '#856404' }}>
            File Validation: {cycle.fileValidation.valid ? 'Passed' : 'Issues Found'}
          </h4>
          {cycle.fileValidation.errors.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {cycle.fileValidation.errors.map((e, i) => (
                <li key={i} style={{ fontSize: 13, marginBottom: 4 }}>{e}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Post-approval action (auto-detected) */}
      {cycle.postApprovalAction && (
        <div style={{ marginBottom: 20, fontSize: 13, color: '#666' }}>
          Auto-detected post-approval action: <strong>{POST_APPROVAL_LABELS[cycle.postApprovalAction] || cycle.postApprovalAction}</strong>
        </div>
      )}

      {/* Content preview */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 16, color: '#123250', marginBottom: 12 }}>Content Preview</h3>
        <DocumentViewer
          user={user}
          cycle={cycle}
          comments={data.comments}
          getAuthHeaders={getAuthHeaders}
          onCommentAdded={() => {}}
          readOnly={true}
          canEditAnyComment={canEditAnyComment}
          refreshKey={dataVersion}
          activeTab={activeTab}
          onActiveTabChange={setActiveTab}
          canDeleteChild={canDeleteChildCycles}
          onChildDeleted={fetchCycle}
        />
      </div>

      {/* AI Pre-Review */}
      {cycle.aiPreReview && (
        <div className="ai-prereview">
          <h4>AI Pre-Review Summary</h4>
          <p>{cycle.aiPreReview}</p>
        </div>
      )}

      {/* Phase 12F — @mention audit trail */}
      {/* 2026-08-26 — ConsultationsPanel moved INTO the viewer's right rail
          (ViewerSidePanel). It was here, below the document, so answering a
          question meant scrolling past the whole item and then losing sight
          of the content you were answering about. Rendering it in both places
          would double-fetch and give two sources of truth for one thread. */}

      {/* Decision section */}
      {/* On an embedded item, that item's business — not the review's. There is
          one pre-screen and it covers the whole review; marking the item seen
          here also satisfies the per-item gate on approval later, so a DM who
          inspects a video at pre-screen is not asked again at the decision. */}
      {activeTab !== 'parent' && (() => {
        const kid = children.find(c => c._id === activeTab);
        if (!kid) return null;
        const seen = (kid.reviewedBy || []).some(
          r => r?.reviewer?.email?.toLowerCase() === (user?.email || '').toLowerCase()
        );
        return (
          <div className="review-section" data-testid="prescreen-child-panel">
            <h3>Reviewed: {kid.contentItem?.name}</h3>
            <div className="viewer-tab-hint">
              <strong>The pre-screen decision is made once, on the main tab</strong>, and covers
              this item too — reviewers, fast track and routing are set for the whole review.
              What is left here is confirming you have seen the item itself.
            </div>
            {seen ? (
              <div style={{ background: '#d4edda', padding: 14, borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 18, color: '#155724' }}>&#10003;</span>
                <strong style={{ color: '#155724', flex: 1, minWidth: 180 }}>You've reviewed this item.</strong>
                <button
                  className="review-btn review-btn-outline"
                  onClick={() => toggleChildReviewed(kid._id, false)}
                  disabled={markingReviewed}
                  style={{ fontSize: 13 }}
                >
                  Undo
                </button>
                <button
                  className="review-btn review-btn-primary"
                  onClick={() => setActiveTab('parent')}
                  style={{ fontSize: 13 }}
                >
                  Back to the pre-screen
                </button>
              </div>
            ) : (
              <button
                className="review-btn review-btn-success"
                onClick={() => toggleChildReviewed(kid._id, true)}
                disabled={markingReviewed}
                style={{ fontSize: 14, padding: '8px 18px' }}
              >
                {markingReviewed ? 'Saving...' : "I've reviewed this item"}
              </button>
            )}
          </div>
        );
      })()}

      {activeTab === 'parent' && (
      <div className="prescreen-decision-panel">
        {/* Q2 v1 — destination override. Paths rendered Parent / Child. */}
        {folderNodes.length > 0 && (
          <div className="review-form-group" style={{ maxWidth: 420 }}>
            <label>
              Destination folder{' '}
              <span style={{ fontSize: 11, color: '#888', fontWeight: 'normal' }}>
                (optional — defaults to the content Type's folder; applies to archive, working file, and Content Hub)
              </span>
            </label>
            <select value={destinationNodeId} onChange={e => setDestinationNodeId(e.target.value)}>
              <option value="">Type default</option>
              {(() => {
                const byId: Record<string, { name: string; parent: string | null }> = {};
                folderNodes.forEach(n => { byId[n._id] = n; });
                const pathOf = (id: string): string => {
                  const parts: string[] = [];
                  let cur: string | null = id;
                  let hops = 0;
                  while (cur && byId[cur] && hops < 10) { parts.unshift(byId[cur].name); cur = byId[cur].parent; hops++; }
                  return parts.join(' / ');
                };
                return folderNodes
                  .map(n => ({ id: n._id, path: pathOf(n._id) }))
                  .sort((a, b) => a.path.localeCompare(b.path))
                  .map(o => <option key={o.id} value={o.id}>{o.path}</option>);
              })()}
            </select>
          </div>
        )}
        <h3>Pre-Screen Decision</h3>
        <p style={{ fontSize: 14, color: '#555', marginBottom: 16 }}>
          Review the content above and decide how to proceed.
        </p>

        <div className="review-form-group">
          <label>Notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Notes on your pre-screen decision..."
            rows={2}
          />
        </div>

        {/* Phase 6B — review deadline. Saves either via its own Save button or
            automatically as part of "Send to Review" / "Fast Track" below. */}
        <DeadlineEditor
          cycleId={cycleId}
          currentDeadline={cycle.reviewDeadline}
          getAuthHeaders={getAuthHeaders}
          onUpdated={fetchCycle}
          onValueChange={setPendingDeadline}
        />

        {/* DM routing (2026-09-02) — assigned decision maker(s), editable at
            pre-screen. Saves via its own button, independent of the
            pre-screen action. Routing only, never access. */}
        <AssignedDmEditor
          cycleId={cycleId}
          current={cycle.assignedDecisionMakers || []}
          getAuthHeaders={getAuthHeaders}
          onUpdated={fetchCycle}
          canEdit
        />

        {/* Reviewer selection for MCL */}
        <div className="review-form-group">
          <label style={{ marginBottom: 8, display: 'block' }}>Assign Reviewers</label>
          <div className="reviewer-picker">
            {reviewers.length === 0 && (
              <p style={{ fontSize: 13, color: '#999', margin: 0 }}>No team members with reviewer permission found.</p>
            )}
            {reviewers.map(m => {
              const isSelected = selectedReviewers.includes(m._id);
              return (
                <button
                  key={m._id}
                  type="button"
                  className={`reviewer-chip ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleReviewer(m._id)}
                >
                  <span className="reviewer-chip-avatar">{m.name.charAt(0)}</span>
                  <span>{m.name}</span>
                  {contexts.length > 0 && !contexts.some(c =>
                    String(c.reviewer?._id || '') === String(m._id) ||
                    (c.reviewer?.email || '').toLowerCase() === (m.email || '').toLowerCase()) && (
                    <span
                      title="No reviewer domain set — the sequence will not wait on this reviewer. Set it under Reviewer Contexts."
                      style={{ fontSize: 11, color: '#b45309' }}
                    >
                      &#9888; no domain
                    </span>
                  )}
                  {isSelected && <span className="reviewer-chip-check">&#10003;</span>}
                </button>
              );
            })}
          </div>
          {selectedReviewers.length > 0 && (
            <p style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
              {selectedReviewers.length} reviewer{selectedReviewers.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>

        <div className="review-action-bar">
          <button
            className="review-btn review-btn-warning"
            onClick={() => handlePreScreen('needs_mcl')}
            disabled={submitting || selectedReviewers.length === 0}
            title={selectedReviewers.length === 0 ? 'Select at least one reviewer' : 'Send to reviewers for assessment'}
          >
            {submitting ? 'Submitting...' : 'Send to Review'}
          </button>
          <button
            className="review-btn review-btn-success"
            onClick={() => handlePreScreen('fast_track')}
            disabled={submitting}
            title="Approve directly without review"
          >
            {submitting ? 'Submitting...' : 'Fast Track (Approve)'}
          </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default PreScreenView;
